import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { Plus, Search, Trophy } from 'lucide-react'
import { COMPETITION_STATES, type CompetitionState } from '@concours/shared'
import {
  Alert,
  Button,
  Can,
  Card,
  Empty,
  FilterRow,
  Input,
  PageHeader,
  Pagination,
  SegmentedPills,
  StatusBadge,
  STATUS_LABELS,
  Table,
  useAuth,
  type Column,
} from '@concours/ui'
import { errorMessage } from '../../lib/errors'
import type { Paginated } from '../../lib/rbac-types'
import { when, type Competition } from '../../lib/competition-types'

export const COMPETITIONS_KEY = ['admin', 'competitions'] as const
const PER_PAGE = 25

type Listing = Paginated<Competition> & {
  meta: Paginated<Competition>['meta'] & { byStatus: Partial<Record<CompetitionState, number>> }
}

/** Liste des concours (TODO phase 6) — limitée au périmètre de l'utilisateur par l'API. */
export function CompetitionsPage() {
  const { t } = useTranslation()
  const { session } = useAuth()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const status = (params.get('statut') ?? 'all') as CompetitionState | 'all'
  const search = params.get('q') ?? ''
  const page = Number(params.get('page') ?? 1) || 1
  const [draft, setDraft] = useState(search)

  const set = (changes: Record<string, string>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const [k, v] of Object.entries(changes)) {
          if (v && v !== 'all') next.set(k, v)
          else next.delete(k)
        }
        if (!('page' in changes)) next.delete('page')
        return next
      },
      { replace: true }
    )

  const list = useQuery({
    queryKey: [...COMPETITIONS_KEY, { status, search, page }],
    queryFn: () =>
      session.api.get<Listing>('/admin/competitions', {
        query: {
          status: status === 'all' ? undefined : status,
          search: search || undefined,
          page,
          perPage: PER_PAGE,
        },
      }),
    placeholderData: keepPreviousData,
  })

  const counts = list.data?.meta.byStatus ?? {}
  const total = Object.values(counts).reduce((sum, n) => sum + (n ?? 0), 0)
  const pills = [
    { value: 'all' as const, label: `${t('competitions.all')} · ${total}` },
    ...COMPETITION_STATES.filter((s) => counts[s] || s === status).map((s) => ({
      value: s,
      label: `${STATUS_LABELS.competition[s].label} · ${counts[s] ?? 0}`,
    })),
  ]

  const columns: Column<Competition>[] = [
    {
      key: 'title',
      header: t('competitions.fields.title'),
      cell: (c) => (
        <div>
          <div style={{ fontWeight: 600 }}>{c.title}</div>
          <span className="perm-code">{c.reference}</span>
        </div>
      ),
    },
    { key: 'session', header: t('competitions.fields.session'), cell: (c) => c.session },
    {
      key: 'status',
      header: t('competitions.status'),
      cell: (c) => <StatusBadge domain="competition" status={c.status} />,
    },
    {
      key: 'period',
      header: t('competitions.period'),
      cell: (c) => (
        <span style={{ fontSize: 'var(--text-sm)' }}>
          {when(c.opensAt)}
          <br />→ {when(c.closesAt)}
        </span>
      ),
    },
    { key: 'seats', header: t('competitions.fields.seats'), numeric: true, cell: (c) => c.seats },
  ]

  return (
    <>
      <PageHeader
        eyebrow={t('competitions.eyebrow')}
        title={t('competitions.title')}
        description={t('competitions.description')}
        actions={
          <Can perm="concours.create">
            <Link to="/concours/nouveau" className="btn btn-primary">
              <Plus aria-hidden="true" /> {t('competitions.new')}
            </Link>
          </Can>
        }
      />
      <Card>
        <FilterRow>
          <SegmentedPills
            label={t('competitions.status')}
            options={pills}
            value={status}
            onChange={(value) => set({ statut: value })}
          />
          <form
            role="search"
            className="row"
            style={{ gap: 'var(--space-2)', alignItems: 'flex-end', marginInlineStart: 'auto' }}
            onSubmit={(e) => {
              e.preventDefault()
              set({ q: draft.trim() })
            }}
          >
            <Input
              label={t('competitions.search')}
              placeholder={t('competitions.searchPlaceholder')}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <Button
              type="submit"
              variant="secondary"
              iconOnly
              icon={<Search />}
              aria-label={t('competitions.search')}
            />
          </form>
        </FilterRow>
        {list.isError && <Alert tone="danger">{errorMessage(list.error)}</Alert>}
        <Table
          columns={columns}
          rows={list.data?.data ?? []}
          rowKey={(c) => c.id}
          loading={list.isPending}
          caption={t('competitions.title')}
          onRowClick={(c) => navigate(`/concours/${c.id}`)}
          empty={<Empty icon={<Trophy aria-hidden="true" />} title={t('competitions.empty')} />}
        />
        {list.data && list.data.meta.total > PER_PAGE && (
          <Pagination
            page={list.data.meta.currentPage}
            perPage={list.data.meta.perPage}
            total={list.data.meta.total}
            onPageChange={(p) => set({ page: String(p) })}
          />
        )}
      </Card>
    </>
  )
}
